import React, { forwardRef, useId, useState } from "react";

/**
 * Enhanced form textarea component with comprehensive styling, accessibility, and UX features.
 *
 * Features:
 * - Clear, accessible error handling
 * - Optional icons
 * - Focus and hover states
 * - Character counter
 * - Comprehensive validation
 * - Label animations
 * - Dark mode support
 * - Resizable textarea
 *
 * @param {Object} props
 * @param {string} props.id - Textarea ID (generated if not provided)
 * @param {string} props.name - Textarea name attribute
 * @param {string} props.label - Label text
 * @param {string} props.value - Textarea value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.required - Whether the textarea is required
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.hint - Helper text
 * @param {number} props.maxLength - Maximum characters
 * @param {boolean} props.disabled - Whether the textarea is disabled
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.iconPosition - Position of icon ('left' or 'right')
 * @param {Function} props.onFocus - Focus handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.className - Additional class names
 * @param {number} props.rows - Number of rows
 * @param {boolean} props.resizable - Whether the textarea is resizable
 * @param {Object} props.validation - Validation rules
 * @param {boolean} props.autoFocus - Whether to auto-focus the textarea
 */
const FormTextarea = forwardRef(
  (
    {
      id,
      name,
      label,
      value,
      onChange,
      required = false,
      placeholder = "",
      error = "",
      hint,
      maxLength,
      disabled = false,
      icon,
      iconPosition = "right",
      onFocus,
      onBlur,
      className = "",
      rows = 4,
      resizable = true,
      validation,
      autoFocus = false,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [localValue, setLocalValue] = useState(value || "");

    const generatedId = useId();
    const textareaId = id ?? `textarea-${name || "field"}-${generatedId}`;

    // Handle textarea change
    const handleChange = (e) => {
      setLocalValue(e.target.value);
      if (onChange) onChange(e);
    };

    // Handle focus events
    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    // Handle blur events
    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    // Calculate padding based on icon position
    const iconPaddingClasses = icon
      ? iconPosition === "left"
        ? "pl-9"
        : "pr-9"
      : "";

    // Character counter
    const renderCharCounter = () => {
      if (!maxLength) return null;

      const count = localValue?.length || 0;
      const isNearLimit = count > maxLength * 0.8;
      const isAtLimit = count >= maxLength;

      const counterClass = isAtLimit
        ? "text-sg-danger"
        : isNearLimit
          ? "text-sg-warn"
          : "text-sg-ink-3";

      return (
        <div className={`font-mono text-[11px] ${counterClass} mt-1 text-right`}>
          {count}/{maxLength}
        </div>
      );
    };

    return (
      <div className={`relative ${className}`}>
        {/* Label with animated float effect */}
        {label && (
          <label
            htmlFor={textareaId}
            className={`sg-field-label ${
              error ? "!text-sg-danger" : isFocused ? "!text-sg-ink" : ""
            }`}
          >
            <div className="flex items-center">
              {label}
              {required && <span className="text-sg-danger ml-1">*</span>}
            </div>
          </label>
        )}

        {/* Textarea container with focus animation */}
        <div className="relative group">
          {/* Left icon with animated color transition */}
          {icon && iconPosition === "left" && (
            <div
              className={`absolute top-3 left-0 pl-3 flex items-start pointer-events-none ${
                isFocused ? "text-sg-ink" : "text-sg-ink-3"
              } transition-colors duration-150`}
            >
              {icon}
            </div>
          )}

          {/* Textarea element with enhanced animations */}
          <textarea
            ref={ref}
            id={textareaId}
            name={name}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            autoFocus={autoFocus}
            onFocus={handleFocus}
            onBlur={handleBlur}
            rows={rows}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              error
                ? `${textareaId}-error`
                : hint
                  ? `${textareaId}-hint`
                  : undefined
            }
            className={`
            sg-field
            ${iconPaddingClasses}
            ${resizable ? "resize-y" : "resize-none"}
          `}
            {...props}
          />

          {/* Right icon with animated color transition */}
          {icon && iconPosition === "right" && (
            <div
              className={`absolute top-3 right-0 pr-3 flex items-start pointer-events-none ${
                isFocused ? "text-sg-ink" : "text-sg-ink-3"
              } transition-colors duration-150`}
            >
              {icon}
            </div>
          )}
        </div>

        {/* Error message with enhanced animation */}
        {error && (
          <div className="overflow-hidden">
            <p
              className="sg-field-msg"
              data-tone="error"
              id={`${textareaId}-error`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </p>
          </div>
        )}

        {/* Helper text with icon */}
        {hint && !error && (
          <div className="overflow-hidden">
            <p
              className="sg-field-msg"
              id={`${textareaId}-hint`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 mt-px flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{hint}</span>
            </p>
          </div>
        )}

        {/* Enhanced character counter with animation */}
        {maxLength && (
          <div className="flex justify-end mt-1.5 overflow-hidden">
            {renderCharCounter()}
          </div>
        )}
      </div>
    );
  },
);

FormTextarea.displayName = "FormTextarea";

export default FormTextarea;
